import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpStudentManagementPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', IconComponent: Icons.Users },
    { id: 'adding-students', name: 'Adding Students', IconComponent: Icons.Plus },
    { id: 'managing-profiles', name: 'Managing Profiles', IconComponent: Icons.PencilSquare },
    { id: 'ehcp-integration', name: 'EHCP Integration', IconComponent: Icons.ClipboardDocumentList },
    { id: 'best-practices', name: 'Best Practices', IconComponent: Icons.LightBulb }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.Users className="w-8 h-8 mr-3" /> Student Management Help</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to managing student profiles and SEN information
              </p>
            </div>
            <Link
              to="/student-organization"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Students
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Student Management Overview</h2>
                <p className="text-gray-600 mb-4">
                  The Student Management system is the foundation of your SEN curriculum management. 
                  It allows you to create comprehensive student profiles, manage SEN information, 
                  integrate EHCPs, and track student progress over time.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ClipboardDocumentList className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Student Profiles</h3>
                    <p className="text-sm text-gray-600 mt-2">Comprehensive student information and SEN details</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Search className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Search & Filter</h3>
                    <p className="text-sm text-gray-600 mt-2">Quick access to student information and records</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ChartBar className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Progress Tracking</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor student development and achievements</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'adding-students' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Adding New Students</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step-by-Step Process</h3>
                    <ol className="text-blue-800 space-y-2 ml-4">
                      <li>1. Navigate to the Students page</li>
                      <li>2. Click the "Add New Student" button</li>
                      <li>3. Fill in the student's basic information:
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Full name and date of birth</li>
                          <li>• School year and class information</li>
                          <li>• Contact details for parents/guardians</li>
                        </ul>
                      </li>
                      <li>4. Add SEN-specific information:
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Primary areas of need</li>
                          <li>• Communication methods</li>
                          <li>• Mobility and accessibility requirements</li>
                        </ul>
                      </li>
                      <li>5. Save the student profile</li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Required Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">Basic Details</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Student's full name</li>
                          <li>• Date of birth</li>
                          <li>• School year</li>
                          <li>• Class/group assignment</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">SEN Information</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Primary areas of need</li>
                          <li>• Communication preferences</li>
                          <li>• Learning support requirements</li>
                          <li>• Medical considerations</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">Important Notes</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• All student information is stored securely</li>
                      <li>• You can edit student details at any time</li>
                      <li>• Student profiles are linked to curricula and progress tracking</li>
                      <li>• Consider adding profile photos for easy identification</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'managing-profiles' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Student Profiles</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.PencilSquare className="w-5 h-5 mr-2" /> Editing Student Information</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Click on any student card to view their full profile</li>
                      <li>• Use the edit button to modify student details</li>
                      <li>• Update SEN information as student needs change</li>
                      <li>• Add notes and observations over time</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Search className="w-5 h-5 mr-2" /> Finding Students</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Use the search bar to find students by name</li>
                      <li>• Filter students by year group or class</li>
                      <li>• Sort students alphabetically or by recent activity</li>
                      <li>• Quick access to frequently viewed students</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Overview</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• View student's curriculum history</li>
                      <li>• Check intervention participation</li>
                      <li>• Monitor achievement milestones</li>
                      <li>• Access progress reports and assessments</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ArrowPath className="w-5 h-5 mr-2" /> Profile Updates</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Regular review of SEN information</li>
                      <li>• Update communication preferences</li>
                      <li>• Modify learning support requirements</li>
                      <li>• Document significant changes or developments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ehcp-integration' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">EHCP Integration</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">What is an EHCP?</h3>
                    <p className="text-blue-800 mb-3">
                      An Education, Health and Care Plan (EHCP) is a legal document that describes a child's 
                      special educational needs and the support they require. It's essential for ensuring 
                      appropriate provision for students with SEN.
                    </p>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Outlines student's specific needs and goals</li>
                      <li>• Details required support and interventions</li>
                      <li>• Sets measurable outcomes and targets</li>
                      <li>• Ensures legal compliance and funding</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">EHCP Management Features</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">Document Management</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Upload and store EHCP documents</li>
                          <li>• Version control and updates</li>
                          <li>• Secure access and sharing</li>
                          <li>• Integration with student profiles</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">Target Tracking</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Monitor EHCP outcomes</li>
                          <li>• Track progress against targets</li>
                          <li>• Generate progress reports</li>
                          <li>• Plan review meetings</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2">Best Practices</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Keep EHCPs up to date with regular reviews</li>
                      <li>• Ensure all staff can access relevant information</li>
                      <li>• Link EHCP targets to curriculum objectives</li>
                      <li>• Document progress and achievements regularly</li>
                      <li>• Prepare for annual review meetings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'best-practices' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Best Practices</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Profile Management</h3>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Keep student information current and accurate</li>
                      <li>• Use consistent naming conventions</li>
                      <li>• Add profile photos for easy identification</li>
                      <li>• Document significant changes and developments</li>
                      <li>• Regular review and updates of SEN information</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Lock className="w-5 h-5 mr-2" /> Data Protection</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Ensure only authorized staff have access</li>
                      <li>• Follow GDPR and data protection guidelines</li>
                      <li>• Secure storage of sensitive information</li>
                      <li>• Regular backup of student data</li>
                      <li>• Audit trail for data access and changes</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Monitoring</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Regular review of student progress</li>
                      <li>• Update learning objectives as needed</li>
                      <li>• Document achievements and milestones</li>
                      <li>• Plan interventions based on progress data</li>
                      <li>• Generate reports for stakeholders</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center"><Icons.Handshake className="w-5 h-5 mr-2" /> Collaboration</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Share relevant information with staff</li>
                      <li>• Involve parents/guardians in planning</li>
                      <li>• Coordinate with external professionals</li>
                      <li>• Regular team meetings and updates</li>
                      <li>• Clear communication channels</li>
                    </ul>
                  </div>

                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Set up regular reminders for profile reviews</li>
                      <li>• Use consistent terminology across all profiles</li>
                      <li>• Create templates for common SEN categories</li>
                      <li>• Regular training for staff on system usage</li>
                      <li>• Backup important documents and information</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpStudentManagementPage; 